import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { TenantProvider } from "@/context/TenantContext";

const Index = lazy(() => import("./pages/Index"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const OrderTracking = lazy(() => import("./pages/OrderTracking"));
const TableMenu = lazy(() => import("./pages/TableMenu"));
const Sobre = lazy(() => import("./pages/Sobre"));
const Privacidade = lazy(() => import("./pages/Privacidade"));
const Termos = lazy(() => import("./pages/Termos"));
const Seguranca = lazy(() => import("./pages/Seguranca"));
const Planos = lazy(() => import("./pages/Planos"));
const NotFound = lazy(() => import("./pages/NotFound"));
const SuperAdminDashboard = lazy(() => import("./pages/SuperAdminDashboard"));
const WaiterDashboard = lazy(() => import("./pages/WaiterDashboard"));

const queryClient = new QueryClient();

const RouteFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <p className="animate-pulse text-sm text-muted-foreground">Carregando...</p>
  </div>
);

const LegacyPlansRedirect = () => {
  const { search, hash } = useLocation();
  return <Navigate to={`/${search}${hash}`} replace />;
};

const App = () => (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <AuthProvider>
          <BrowserRouter>
            <TenantProvider>
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  {/* Public Store Routes */}
                  <Route path="/" element={<Planos />} />
                  <Route path="/:slug" element={<Index />} />

                  {/* Mesa (QR na mesa) */}
                  <Route path="/:slug/mesa/:mesa" element={<TableMenu />} />
                  
                  {/* Admin Routes */}
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/admin/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/super-admin" element={<SuperAdminDashboard />} />
                  <Route path="/garcom" element={<WaiterDashboard />} />
                  
                  {/* Order Tracking */}
                  <Route path="/pedido/:orderNumber" element={<OrderTracking />} />
                  <Route path="/:slug/pedido/:orderNumber" element={<OrderTracking />} />
                  
                  <Route path="/sobre" element={<Sobre />} />
                  <Route path="/:slug/sobre" element={<Sobre />} />

                  {/* Legal & Segurança */}
                  <Route path="/privacidade" element={<Privacidade />} />
                  <Route path="/termos" element={<Termos />} />
                  <Route path="/seguranca" element={<Seguranca />} />

                  {/* Landing comercial */}
                  <Route path="/planos" element={<LegacyPlansRedirect />} />
                  
                  {/* Catch-all */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </TenantProvider>
          </BrowserRouter>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
);

export default App;
